import { useEffect, useState } from "react";
import "./App.css";
import {
  authenticate,
  getDivisions,
  getUserMe,
} from "./utilities/genesysCloudUtils";
import { buildCsv, appendExportMetadata, buildExportFilename, downloadCsv } from "./utilities/csvExportUtils";
import { buildExportRows, buildExportRowsFromRecords, getExportAdapter } from "./utilities/catalogExportAdapters";
import { Loading } from "./components/Loading";
import { LeftPanel } from "./components/LeftPanel";
import { Body } from "./components/Body";
import { ExportToast } from "./components/ExportToast";

const DARK_THEME_ENABLED_KEY = "dark-theme";

function App() {
  const [auth, setAuth] = useState(false);
  const [loadingText] = useState("Authenticating...");
  const [darkThemeEnabled, setDarkThemeEnabled] = useState(
    localStorage.getItem(DARK_THEME_ENABLED_KEY) === "true"
  );
  const [permissions, setPermissions] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [checkedDivisions, setCheckedDivisions] = useState([]);
  const [opts, setOpts] = useState({});
  const [org, setOrg] = useState(null);
  const [tabName, setTabName] = useState("divisions-tab");
  const [filters, setFilters] = useState({});
  const [selectedExportRows, setSelectedExportRows] = useState({});
  const [exportStatus, setExportStatus] = useState({ loading: false, message: "", error: "" });
  const [isVisible, setIsVisible] = useState({ divisions: false, object: false, applyButton: false });

  const clearExportStatus = () => {
    setExportStatus((currentStatus) => ({ ...currentStatus, message: "", error: "" }));
  };

  const setActiveTabName = (nextTabName) => {
    setFilters({});
    setTabName(nextTabName);
  };

  const onExportCsv = async ({ mode, records, exportContext: exportContextOverrides = {} }) => {
    const adapter = getExportAdapter(tabName);

    if (!adapter) {
      setExportStatus({ loading: false, message: "", error: "Export is not configured for this tab yet." });
      return;
    }

    if (mode === "selected" && Object.keys(selectedExportRows[tabName] || {}).length === 0) {
      setExportStatus({ loading: false, message: "", error: "Select at least one row before exporting selected records." });
      return;
    }

    if (mode === "current" && (!records || records.length === 0)) {
      setExportStatus({ loading: false, message: "", error: "There are no rows on this page to export." });
      return;
    }

    setExportStatus({ loading: true, message: "Preparing export...", error: "" });

    try {
      const exportedAt = new Date().toISOString();
      const selectedRows = Object.values(selectedExportRows[tabName] || {});
      const currentPageRows = records || [];
      const exportContext = {
        checkedDivisions,
        divisions,
        filters,
        opts,
        org,
        permissions,
        selectedExportRows: selectedExportRows[tabName] || {},
        tabName,
        ...exportContextOverrides,
      };
      const { rows, capped, columns } = mode === "selected"
        ? await buildExportRowsFromRecords(adapter, selectedRows, exportContext)
        : mode === "current"
          ? await buildExportRowsFromRecords(adapter, currentPageRows, exportContext)
          : await buildExportRows(adapter, exportContext);
      const rowsWithMetadata = appendExportMetadata(rows, {
        org,
        tabLabel: adapter.label,
        exportedAt,
      });
      const csv = buildCsv(columns, rowsWithMetadata);
      const filename = buildExportFilename({ org, tabLabel: adapter.label });

      downloadCsv({ filename, csv });
      setExportStatus({
        loading: false,
        message: capped ? `Exported ${rowsWithMetadata.length} rows. Results were capped at 10,000 records.` : `Exported ${rowsWithMetadata.length} rows.`,
        error: "",
      });
    } catch (error) {
      console.error("CSV export failed:", error);
      setExportStatus({ loading: false, message: "", error: error.message || "CSV export failed." });
    }
  };

  useEffect(() => {
    if (!org) {
      authenticate("DEV")
        .then(() => {
          setAuth(true);
        })
        .then(() => {
          getDivisions().then((data) => {
            setDivisions(data.entities);
          });
          getUserMe().then((data) => {
            setPermissions(data.authorization.permissions);
          });
        });
    } else {
      console.log("ORG:", org);
      authenticate(org)
        .then(() => {
          setAuth(true);
        })
        .then(() => {
          getDivisions().then((data) => {
            setDivisions(data.entities);
          });
          getUserMe().then((data) => {
            setPermissions(data.authorization.permissions);
          });
        });
    }
  }, [org]);

  useEffect(() => {
    // Save theme setting
    localStorage.setItem(
      DARK_THEME_ENABLED_KEY,
      darkThemeEnabled ? "true" : ""
    );
  }, [darkThemeEnabled]);

  // useEffect(() => {
  //   console.log("Checked Divisions: ", checkedDivisions);
  // }, [checkedDivisions]);

  return (
    <div
      className={`app-container my-app${darkThemeEnabled ? "-dark" : "-light"
        } disappearing-scrollbars`}
    >
      {auth ? (
        <>
          <LeftPanel
            darkThemeEnabled={darkThemeEnabled}
            setDarkThemeEnabled={setDarkThemeEnabled}
            divisions={divisions}
            checkedDivisions={checkedDivisions}
            setCheckedDivisions={setCheckedDivisions}
            opts={opts}
            setOpts={setOpts}
            setOrg={setOrg}
            tabName={tabName}
            filters={filters}
            setFilters={setFilters}
            isVisible={isVisible}
          />
          <div className="app">
            <Body
              divisions={divisions}
              checkedDivisions={checkedDivisions}
              opts={opts}
              setOpts={setOpts}
              permissions={permissions}
              org={org}
              tabName={tabName}
              setTabName={setActiveTabName}
              filters={filters}
              setIsVisible={setIsVisible}
              exportStatus={exportStatus}
              onExportCsv={onExportCsv}
              selectedExportRows={selectedExportRows}
              setSelectedExportRows={setSelectedExportRows}
            />
          </div>
          <ExportToast exportStatus={exportStatus} onDismiss={clearExportStatus} />
        </>
      ) : (
        <div className="auth-loading-screen">
          <div className="auth-loading-panel">
            <span className="auth-loading-eyebrow">Maximus</span>
            <h1 className="auth-loading-title">Genesys Cloud Master Catalogue</h1>
            <Loading loadingText={loadingText} />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
